'use client';

import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useAccountManager } from '@/hooks/use-account-manager';
import { useOrgSlug } from '@/hooks/use-org-slug';
import { ORG_ROUTES } from '@/config/router';
import type { DateRange } from 'react-day-picker';

import {
  useBillingReports,
  useBillingPreview,
  useAccountPlan
} from './hooks/use-billing-data';
import {
  useRetryReport,
  useDeleteReport,
  useCustomerPortal,
  usePaymentPortal,
  useInvoiceLink
} from './hooks/use-billing-mutations';

import { UTCTimeDisplay } from './components/UTCTimeDisplay';
import { CurrentPeriodPreview } from './components/CurrentPeriodPreview';
import { BillingReportsList } from './components/BillingReportsList';
import { PlanDetailsTab } from './components/PlanDetailsTab';
import { PaymentMethodsTab } from './components/PaymentMethodsTab';
import { CreditsTab } from './components/CreditsTab';
import { buildPricingCategories } from './components/pricing-config';
import { PageHeader } from '@/components/ui/page-header';

import { PERMISSIONS, usePerm } from '@/hooks/use-perm';
import {
  type BillingTabKey,
  isRestrictedBillingTab,
  parseBillingTab,
} from './billing-tabs';

const PDF_TAB_CLOSE_DELAY_MS = 5000;

export default function BillingPage() {
  const { t } = useTranslation('billing');
  const { can, isLoaded } = usePerm();
  const canViewBilling = can(PERMISSIONS.BILLING_VIEW);
  const { isAccountManager } = useAccountManager();
  const { urlSlug } = useOrgSlug();
  const [searchParams, setSearchParams] = useSearchParams();
  const activeTab = parseBillingTab(searchParams.get('tab'), canViewBilling);
  const setActiveTab = (v: BillingTabKey) => {
    if (!canViewBilling && isRestrictedBillingTab(v)) return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', v);
    setSearchParams(next, { replace: true });
  };

  const [currentUTCTime, setCurrentUTCTime] = useState<Date | null>(null);
  const [isMounted, setIsMounted] = useState(false);
  const [showCurrentBreakdown, setShowCurrentBreakdown] = useState(false);

  const [customDateRange, setCustomDateRange] = useState<DateRange>({ from: undefined, to: undefined });
  const [isLoadingCustomPreview, setIsLoadingCustomPreview] = useState(false);
  const [previewPeriodStart, setPreviewPeriodStart] = useState<string | undefined>(undefined);
  const [previewPeriodEnd, setPreviewPeriodEnd] = useState<string | undefined>(undefined);

  useEffect(() => {
    setIsMounted(true);
    setCurrentUTCTime(new Date());

    const timer = setInterval(() => {
      setCurrentUTCTime(new Date());
    }, 1000);

    return () => clearInterval(timer);
  }, []);

  // Sync URL when permission forces payment-methods (#TAG-1807).
  useEffect(() => {
    if (!isLoaded || canViewBilling) return;
    if (searchParams.get('tab') === 'payment-methods') return;
    const next = new URLSearchParams(searchParams);
    next.set('tab', 'payment-methods');
    setSearchParams(next, { replace: true });
  }, [isLoaded, canViewBilling, searchParams, setSearchParams]);

  const { reports, refetchReports, isLoadingReports } = useBillingReports(canViewBilling);
  const { preview, refetchPreview, isFetchingPreview } = useBillingPreview(
    previewPeriodStart,
    previewPeriodEnd,
    canViewBilling,
  );
  const { accountPlan, isLoadingAccountPlan } = useAccountPlan(canViewBilling);

  const retryReportMutation = useRetryReport(() => setTimeout(() => refetchReports(), 3000));
  const deleteReportMutation = useDeleteReport(() => refetchReports());
  const openCustomerPortalMutation = useCustomerPortal();
  const openPaymentPortalMutation = usePaymentPortal();
  const invoiceLinkMutation = useInvoiceLink();
  const [openingInvoiceReportId, setOpeningInvoiceReportId] = useState<string | null>(null);

  const buildReturnUrl = (): string | null => {
    if (!urlSlug) return null;
    return `${window.location.origin}${ORG_ROUTES(urlSlug).BILLING()}?tab=payment-methods`;
  };

  const handleRetryReport = (reportId: string, reportAccountId: string) => {
    retryReportMutation.mutate({ reportId, accountId: reportAccountId });
  };

  const handleDeleteReport = (reportId: string, reportAccountId: string) => {
    if (!window.confirm(t('confirms.deleteReport'))) {
      return;
    }
    deleteReportMutation.mutate({ reportId, accountId: reportAccountId });
  };

  const handleUpdatePayment = () => {
    const returnUrl = buildReturnUrl();
    if (!returnUrl) {
      console.error('No organization slug found - cannot construct return URL');
      toast.error(t('toasts.orgNotFoundUpdatePayment'));
      return;
    }
    openPaymentPortalMutation.mutate({ returnUrl });
  };

  // Stripe expires the invoice links we stored at issue time, so they are resolved
  // on click instead of being read from the row. The tab is opened BEFORE the await:
  // Safari and Firefox block a `window.open` that follows one. #TAG-2261
  const handleOpenInvoice = async (reportId: string, target: 'hosted' | 'pdf') => {
    const tab = window.open('', '_blank');
    if (!tab) {
      toast.error(t('toasts.invoicePopupBlocked'));
      return;
    }

    setOpeningInvoiceReportId(reportId);
    try {
      const link = await invoiceLinkMutation.mutateAsync({ reportId });
      const url = target === 'pdf' ? link.pdf : link.url;
      if (!url) {
        throw new Error(t('toasts.invoiceLinkUnavailable'));
      }
      tab.location.href = url;
      if (target === 'pdf') {
        // Stripe serves the PDF as an attachment, so the tab has nothing to render and
        // would sit on about:blank forever. Once the response headers arrive the browser
        // hands the download to its own manager, and closing the tab no longer cancels
        // it — hence the delay rather than an immediate close.
        setTimeout(() => tab.close(), PDF_TAB_CLOSE_DELAY_MS);
      }
      // Resolving also wrote back the provider's status, which drives the badge and
      // the Update Payment button — without this the row contradicts what we just
      // stored until the merchant refreshes by hand.
      refetchReports();
    } catch (error) {
      tab.close();
      toast.error(error instanceof Error ? error.message : t('toasts.invoiceOpenFailed'));
    } finally {
      setOpeningInvoiceReportId(null);
    }
  };

  const handleOpenCustomerPortal = () => {
    const returnUrl = buildReturnUrl();
    if (!returnUrl) {
      console.error('No organization slug found - cannot construct return URL');
      toast.error(t('toasts.orgNotFoundOpenCustomerPortal'));
      return;
    }
    openCustomerPortalMutation.mutate({ returnUrl });
  };

  const handleApplyCustomRange = async () => {
    if (!customDateRange.from || !customDateRange.to) {
      toast.error(t('toasts.selectDates'));
      return;
    }

    setIsLoadingCustomPreview(true);
    const fromDate = new Date(Date.UTC(
      customDateRange.from.getUTCFullYear(),
      customDateRange.from.getUTCMonth(),
      customDateRange.from.getUTCDate(),
      0, 0, 0, 0
    ));
    const toDate = new Date(Date.UTC(
      customDateRange.to.getUTCFullYear(),
      customDateRange.to.getUTCMonth(),
      customDateRange.to.getUTCDate(),
      23, 59, 59, 999
    ));

    setPreviewPeriodStart(fromDate.toISOString());
    setPreviewPeriodEnd(toDate.toISOString());

    await refetchPreview();
    setIsLoadingCustomPreview(false);
  };

  const handleResetToCurrentWeek = async () => {
    setCustomDateRange({ from: undefined, to: undefined });
    setPreviewPeriodStart(undefined);
    setPreviewPeriodEnd(undefined);
    setIsLoadingCustomPreview(true);
    await refetchPreview();
    setIsLoadingCustomPreview(false);
  };

  const pricingCategories = buildPricingCategories(accountPlan);

  return (
    <div className="flex flex-col space-y-4 sm:space-y-6">
      <PageHeader title={t('page.title')} description={t('page.description')} />

      <Tabs value={activeTab} onValueChange={(v) => setActiveTab(v as BillingTabKey)} className="w-full">
        <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
          <TabsList>
            {canViewBilling ? (
              <>
                <TabsTrigger value="reports">{t('tabs.reports')}</TabsTrigger>
                <TabsTrigger value="plan-details">{t('tabs.planDetails')}</TabsTrigger>
                <TabsTrigger value="credits">{t('tabs.credits')}</TabsTrigger>
              </>
            ) : null}
            <TabsTrigger value="payment-methods">{t('tabs.paymentMethods')}</TabsTrigger>
          </TabsList>
          <UTCTimeDisplay currentTime={currentUTCTime} isMounted={isMounted} />
        </div>

        {canViewBilling ? (
          <>
            <TabsContent value="reports" className="space-y-6">
              <CurrentPeriodPreview
                preview={preview}
                isLoading={isLoadingCustomPreview}
                isFetching={isFetchingPreview}
                showBreakdown={showCurrentBreakdown}
                onToggleBreakdown={() => setShowCurrentBreakdown(!showCurrentBreakdown)}
                isAccountManager={isAccountManager}
                customDateRange={customDateRange}
                onDateRangeChange={setCustomDateRange}
                onApplyCustomRange={handleApplyCustomRange}
                onResetToCurrentWeek={handleResetToCurrentWeek}
                isMounted={isMounted}
              />

              <BillingReportsList
                reports={reports}
                isLoading={isLoadingReports}
                isAccountManager={isAccountManager}
                onRefresh={refetchReports}
                onRetry={handleRetryReport}
                onDelete={handleDeleteReport}
                onUpdatePayment={handleUpdatePayment}
                onOpenInvoice={handleOpenInvoice}
                isRetrying={retryReportMutation.isPending}
                isDeleting={deleteReportMutation.isPending}
                isUpdatingPayment={openPaymentPortalMutation.isPending}
                openingInvoiceReportId={openingInvoiceReportId}
              />
            </TabsContent>

            <TabsContent value="plan-details" className="space-y-6">
              <PlanDetailsTab
                accountPlan={accountPlan}
                isLoadingAccountPlan={isLoadingAccountPlan}
                pricingCategories={pricingCategories}
              />
            </TabsContent>

            <TabsContent value="credits" className="space-y-6">
              <CreditsTab />
            </TabsContent>
          </>
        ) : null}

        <TabsContent value="payment-methods" className="space-y-6">
          <PaymentMethodsTab
            isAccountManager={isAccountManager}
            onOpenCustomerPortal={handleOpenCustomerPortal}
            isOpeningCustomerPortal={openCustomerPortalMutation.isPending}
          />
        </TabsContent>
      </Tabs>
    </div>
  );
}
